import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Typography, Button, Chip, LinearProgress, Collapse, Alert } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import BuildIcon from '@mui/icons-material/Build';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Select from 'react-select';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import toast from 'react-hot-toast';

const TableList = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showImport, setShowImport] = useState(true);
  const [previewTable, setPreviewTable] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showWrangling, setShowWrangling] = useState(false);
  const [wranglingTable, setWranglingTable] = useState(null);
  const [wranglingColumns, setWranglingColumns] = useState([]);
  const [wranglingAction, setWranglingAction] = useState('sort');
  const [wranglingConfig, setWranglingConfig] = useState({ sortCol: '', sortDir: 'asc', newColName: '', formula: '', col1: '', col2: '', separator: '_', dateCol: '', extractPart: 'year' });
  const [wranglingResult, setWranglingResult] = useState(null);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/tables/list');
      setTables(response.data);
    } catch (error) {
      toast.error('Failed to fetch tables');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTables(); }, []);

  const loadWranglingColumns = async (tableId) => {
    try {
      const res = await axios.get(`/api/v1/tables/${tableId}/preview?rows=1`);
      setWranglingColumns(res.data.columns || []);
    } catch (err) { toast.error('Failed to load columns'); }
  };

  const handleOpenWrangling = (tableId) => {
    setWranglingTable(tableId);
    setShowWrangling(true);
    loadWranglingColumns(tableId);
  };

  const handleWranglingApply = async () => {
    if (!wranglingTable) return;
    setLoading(true);
    try {
      const res = await axios.post(`/api/v1/tables/${wranglingTable}/wrangle`, {
        action: wranglingAction,
        config: wranglingConfig
      });
      setWranglingResult(res.data);
      toast.success(res.data.message || 'Transformation applied');
      fetchTables();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const handlePreview = async (id) => {
    if (previewTable === id) { setPreviewTable(null); setPreviewData(null); return; }
    setPreviewLoading(true);
    setPreviewTable(id);
    try {
      const res = await axios.get(`/api/v1/tables/${id}/preview`);
      setPreviewData(res.data);
    } catch (err) {
      toast.error('Failed to load preview');
      setPreviewTable(null);
    } finally { setPreviewLoading(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try {
      await axios.delete(`/api/v1/tables/${id}`);
      toast.success('Deleted');
      fetchTables();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      setUploading(true);
      setUploadProgress(0);
      const formData = new FormData();
      formData.append('file', file);
      try {
        await axios.post('/api/v1/tables/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          },
        });
        toast.success(`Uploaded ${file.name}`);
        fetchTables();
      } catch (error) {
        toast.error(`Failed: ${file.name}`);
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 10 * 1024 * 1024 * 1024,
    disabled: uploading
  });

  const groupStyle = {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    marginBottom: '16px',
    overflow: 'hidden'
  };

  const groupHeaderStyle = {
    background: '#f5f7fa',
    padding: '12px 16px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '14px',
    color: '#2c3e50'
  };

  const groupContentStyle = {
    padding: '16px',
    background: 'white'
  };

  return (
    <Box sx={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#2c3e50' }}>
        📊 Import & Data Management
      </Typography>

      {/* Import Section */}
      <div style={groupStyle}>
        <div style={groupHeaderStyle} onClick={() => setShowImport(!showImport)}>
          <span>📥 Import Files</span>
          <span>{showImport ? <ExpandLess /> : <ExpandMore />}</span>
        </div>
        <Collapse in={showImport}>
          <div style={groupContentStyle}>
            <Paper
              {...getRootProps()}
              sx={{
                p: 4,
                textAlign: 'center',
                cursor: uploading ? 'wait' : 'pointer',
                backgroundColor: isDragActive ? '#e3f2fd' : '#fafafa',
                border: '2px dashed',
                borderColor: isDragActive ? '#1abc9c' : '#ddd',
                borderRadius: 2,
                opacity: uploading ? 0.7 : 1,
                transition: 'all 0.3s',
                '&:hover': { borderColor: '#1abc9c', backgroundColor: '#f5f5f5' }
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 52, color: '#1abc9c', mb: 1 }} />
              {uploading ? (
                <Box>
                  <Typography variant="body1" gutterBottom fontWeight={500}>Uploading...</Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 1, maxWidth: 400, mx: 'auto', height: 6, borderRadius: 3 }} />
                  <Typography variant="body2" color="textSecondary">{uploadProgress}%</Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom fontWeight={500}>
                    {isDragActive ? 'Drop the file here' : 'Drag & drop your file here, or click to browse'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Supported: CSV, Excel, JSON, Parquet, SAS, RDS (Max 10 Go)
                  </Typography>
                </>
              )}
            </Paper>
          </div>
        </Collapse>
      </div>

      {/* Tables Section */}
      <div style={groupStyle}>
        <div style={{ ...groupHeaderStyle, cursor: 'default' }}>
          <span>📋 My Tables</span>
          <Button startIcon={<RefreshIcon />} onClick={fetchTables} size="small" sx={{ fontSize: '11px' }}>Refresh</Button>
        </div>
        <div style={groupContentStyle}>
          <TableContainer sx={{ borderRadius: '4px', border: '1px solid #e0e0e0' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#34495e' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Actions</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Filename</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Size</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Rows</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Columns</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: '#95a5a6' }}>Loading...</TableCell></TableRow>
                ) : tables.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: '#95a5a6' }}>No tables uploaded yet. Use the import section above.</TableCell></TableRow>
                ) : tables.map((t) => (
                  <React.Fragment key={t.id}>
                    <TableRow hover sx={{ '&:last-child td': { borderBottom: previewTable === t.id ? 0 : undefined } }}>
                      <TableCell>
                        <IconButton onClick={() => handlePreview(t.id)} size="small" sx={{ color: previewTable === t.id ? '#e74c3c' : '#3498db' }}>
                          {previewTable === t.id ? <CloseIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                        <IconButton onClick={() => handleOpenWrangling(t.id)} size="small" sx={{ color: '#f39c12' }} title="Data Wrangling">
                          <BuildIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(t.id, t.original_filename)} size="small" sx={{ color: '#e74c3c' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontSize: '12px', fontWeight: 500 }}>{t.original_filename}</TableCell>
                      <TableCell><Chip label={formatSize(t.file_size)} size="small" sx={{ fontSize: '10px', background: '#ecf0f1' }} /></TableCell>
                      <TableCell sx={{ fontSize: '12px' }}>{t.row_count?.toLocaleString()}</TableCell>
                      <TableCell sx={{ fontSize: '12px' }}>{t.column_count}</TableCell>
                    </TableRow>
                  {previewTable === t.id && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 2, background: '#f0f4f8', borderBottom: '2px solid #1abc9c' }}>
                        {previewLoading ? (
                          <Box sx={{ textAlign: 'center' }}>
                            <LinearProgress sx={{ maxWidth: 200, mx: 'auto', mb: 1 }} />
                            <Typography variant="body2" color="textSecondary">Loading preview...</Typography>
                          </Box>
                        ) : previewData ? (
                          <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#2c3e50' }}>
                              📋 Preview: {t.original_filename} ({previewData.total_rows?.toLocaleString()} rows)
                            </Typography>
                            <Box sx={{ overflowX: 'auto', maxHeight: 250, border: '1px solid #ddd', borderRadius: '4px', background: 'white' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                <thead>
                                  <tr style={{ background: '#34495e', color: 'white', position: 'sticky', top: 0 }}>
                                    <th style={{ padding: '8px 12px', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' }}>#</th>
                                    {previewData.columns?.map((col) => (
                                      <th key={col} style={{ padding: '8px 12px', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' }}>{col}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {previewData.rows?.map((row, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                      <td style={{ padding: '6px 12px', color: '#95a5a6' }}>{i + 1}</td>
                                      {previewData.columns?.map((col) => (
                                        <td key={col} style={{ padding: '6px 12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {row[col] !== null && row[col] !== undefined ? String(row[col]) : '-'}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </Box>
                          </Box>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>

      {/* Data Wrangling Section */}
      <div style={{ ...groupStyle, marginTop: '24px' }}>
        <div style={groupHeaderStyle} onClick={() => setShowWrangling(!showWrangling)}>
          <span>🔧 Data Wrangling</span>
          <span>{showWrangling ? <ExpandLess /> : <ExpandMore />}</span>
        </div>
        <Collapse in={showWrangling}>
          <div style={groupContentStyle}>
            <Typography variant="body2" sx={{ mb: 2, color: '#7f8c8d' }}>
              Select a table and apply basic transformations. Changes are saved to the original file.
            </Typography>

            {/* Table selector */}
            <Box sx={{ mb: 2, maxWidth: 400 }}>
              <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Select Table</label>
              <select 
                value={wranglingTable || ''} 
                onChange={(e) => handleOpenWrangling(parseInt(e.target.value))}
                style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="">-- Choose a table --</option>
                {tables.map(t => (
                  <option key={t.id} value={t.id}>{t.original_filename}</option>
                ))}
              </select>
            </Box>

            {wranglingTable && (
              <>
                {/* Action selector */}
                <Box sx={{ mb: 2, maxWidth: 400 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Action</label>
                  <select 
                    value={wranglingAction} 
                    onChange={(e) => setWranglingAction(e.target.value)}
                    style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="sort">Sort</option>
                    <option value="extract_date">Extract from Date (Year/Month/Quarter)</option>
                    <option value="concat_columns">Concat Columns (A + B)</option>
                    <option value="math_operation">Math Operation (A +, -, *, / B)</option>
                    <option value="uppercase">UPPERCASE Column</option>
                    <option value="lowercase">lowercase Column</option>
                    <option value="round">Round Numbers</option>
                    <option value="fill_na">Fill Missing Values</option>
                    <option value="drop_columns">Delete Columns</option>
                  </select>
                </Box>

                {/* Config based on action */}
                <Box sx={{ p: 2, background: '#f9f9f9', borderRadius: '8px', mb: 2 }}>
                  {wranglingAction === 'sort' && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                      <Box>
                        <label style={{ fontSize: '11px' }}>Column</label>
                        <select value={wranglingConfig.sortCol} onChange={(e) => setWranglingConfig(p => ({...p, sortCol: e.target.value}))} style={{ width: '200px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <option value="">-- Select --</option>
                          {wranglingColumns.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Box>
                      <Box>
                        <label style={{ fontSize: '11px' }}>Direction</label>
                        <select value={wranglingConfig.sortDir} onChange={(e) => setWranglingConfig(p => ({...p, sortDir: e.target.value}))} style={{ width: '150px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <option value="asc">Ascending</option>
                          <option value="desc">Descending</option>
                        </select>
                      </Box>
                    </Box>
                  )}

                  {wranglingAction === 'extract_date' && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                      <Box>
                        <label style={{ fontSize: '11px' }}>Date Column</label>
                        <select value={wranglingConfig.dateCol} onChange={(e) => setWranglingConfig(p => ({...p, dateCol: e.target.value}))} style={{ width: '200px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <option value="">-- Select --</option>
                          {wranglingColumns.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Box>
                      <Box>
                        <label style={{ fontSize: '11px' }}>Extract</label>
                        <select value={wranglingConfig.extractPart} onChange={(e) => setWranglingConfig(p => ({...p, extractPart: e.target.value}))} style={{ width: '150px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <option value="year">Year</option>
                          <option value="month">Month</option>
                          <option value="quarter">Quarter</option>
                          <option value="day">Day</option>
                          <option value="weekday">Weekday</option>
                        </select>
                      </Box>
                      <Box>
                        <label style={{ fontSize: '11px' }}>New Column Name</label>
                        <input type="text" value={wranglingConfig.newColName} onChange={(e) => setWranglingConfig(p => ({...p, newColName: e.target.value}))} placeholder="e.g. year_def" style={{ width: '150px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} />
                      </Box>
                    </Box>
                  )}

                  {wranglingAction === 'concat_columns' && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <Box>
                        <label style={{ fontSize: '11px' }}>Column A</label>
                        <select value={wranglingConfig.col1} onChange={(e) => setWranglingConfig(p => ({...p, col1: e.target.value}))} style={{ width: '200px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <option value="">-- Select --</option>
                          {wranglingColumns.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Box>
                      <Box>
                        <label style={{ fontSize: '11px' }}>Separator</label>
                        <input type="text" value={wranglingConfig.separator} onChange={(e) => setWranglingConfig(p => ({...p, separator: e.target.value}))} style={{ width: '60px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} />
                      </Box>
                      <Box>
                        <label style={{ fontSize: '11px' }}>Column B</label>
                        <select value={wranglingConfig.col2} onChange={(e) => setWranglingConfig(p => ({...p, col2: e.target.value}))} style={{ width: '200px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <option value="">-- Select --</option>
                          {wranglingColumns.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Box>
                      <Box>
                        <label style={{ fontSize: '11px' }}>New Column Name</label>
                        <input type="text" value={wranglingConfig.newColName} onChange={(e) => setWranglingConfig(p => ({...p, newColName: e.target.value}))} placeholder="combined_col" style={{ width: '150px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} />
                      </Box>
                    </Box>
                  )}

                  {wranglingAction === 'math_operation' && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <Box>
                        <label style={{ fontSize: '11px' }}>Column A</label>
                        <select value={wranglingConfig.col1} onChange={(e) => setWranglingConfig(p => ({...p, col1: e.target.value}))} style={{ width: '200px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <option value="">-- Select --</option>
                          {wranglingColumns.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Box>
                      <Box>
                        <label style={{ fontSize: '11px' }}>Operation</label>
                        <select value={wranglingConfig.operator || '+'} onChange={(e) => setWranglingConfig(p => ({...p, operator: e.target.value}))} style={{ width: '80px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <option value="+">+</option>
                          <option value="-">-</option>
                          <option value="*">×</option>
                          <option value="/">÷</option>
                        </select>
                      </Box>
                      <Box>
                        <label style={{ fontSize: '11px' }}>Column B</label>
                        <select value={wranglingConfig.col2} onChange={(e) => setWranglingConfig(p => ({...p, col2: e.target.value}))} style={{ width: '200px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <option value="">-- Select --</option>
                          {wranglingColumns.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Box>
                      <Box>
                        <label style={{ fontSize: '11px' }}>New Column Name</label>
                        <input type="text" value={wranglingConfig.newColName} onChange={(e) => setWranglingConfig(p => ({...p, newColName: e.target.value}))} placeholder="result_col" style={{ width: '150px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} />
                      </Box>
                    </Box>
                  )}

                  {(wranglingAction === 'uppercase' || wranglingAction === 'lowercase' || wranglingAction === 'round') && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                      <Box>
                        <label style={{ fontSize: '11px' }}>Column</label>
                        <select value={wranglingConfig.col1} onChange={(e) => setWranglingConfig(p => ({...p, col1: e.target.value}))} style={{ width: '200px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <option value="">-- Select --</option>
                          {wranglingColumns.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Box>
                      {wranglingAction === 'round' && (
                        <Box>
                          <label style={{ fontSize: '11px' }}>Decimals</label>
                          <input type="number" value={wranglingConfig.decimals || 2} onChange={(e) => setWranglingConfig(p => ({...p, decimals: parseInt(e.target.value)}))} style={{ width: '80px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} />
                        </Box>
                      )}
                    </Box>
                  )}

                  {wranglingAction === 'fill_na' && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                      <Box>
                        <label style={{ fontSize: '11px' }}>Column</label>
                        <select value={wranglingConfig.col1} onChange={(e) => setWranglingConfig(p => ({...p, col1: e.target.value}))} style={{ width: '200px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <option value="">-- Select --</option>
                          {wranglingColumns.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Box>
                      <Box>
                        <label style={{ fontSize: '11px' }}>Fill Value</label>
                        <input type="text" value={wranglingConfig.fillValue || '0'} onChange={(e) => setWranglingConfig(p => ({...p, fillValue: e.target.value}))} style={{ width: '120px', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} />
                      </Box>
                    </Box>
                  )}

                  {wranglingAction === 'drop_columns' && (
                    <Box sx={{ maxWidth: 500 }}>
                      <label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Columns to Delete</label>
                      <Select
                        isMulti
                        options={wranglingColumns.map(c => ({ value: c, label: c }))}
                        value={(wranglingConfig.dropCols || []).map(c => ({ value: c, label: c }))}
                        onChange={(selected) => setWranglingConfig(p => ({...p, dropCols: selected ? selected.map(s => s.value) : []}))}
                        styles={{
                          control: (b) => ({ ...b, minHeight: '32px', fontSize: '12px', borderColor: '#ddd' }),
                          menu: (b) => ({ ...b, zIndex: 9999 }),
                          multiValue: (b) => ({ ...b, backgroundColor: '#e74c3c', color: 'white', borderRadius: 3 }),
                          multiValueLabel: (b) => ({ ...b, color: 'white', fontSize: '10px' }),
                          multiValueRemove: (b) => ({ ...b, color: 'white', ':hover': { backgroundColor: '#c0392b', color: 'white' } }),
                          placeholder: (b) => ({ ...b, fontSize: '11px', color: '#bbb' }),
                          menuPortal: (b) => ({ ...b, zIndex: 9999 })
                        }}
                        menuPortalTarget={document.body}
                        placeholder="Search and select columns to delete..."
                      />
                      {wranglingConfig.dropCols?.length > 0 && (
                        <Typography variant="caption" sx={{ color: '#e74c3c', mt: 0.5, display: 'block' }}>
                          ⚠️ {wranglingConfig.dropCols.length} column(s) will be permanently deleted
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                <Button 
                  variant="contained" 
                  startIcon={<PlayArrowIcon />}
                  onClick={handleWranglingApply}
                  disabled={loading}
                  sx={{ background: '#1abc9c', '&:hover': { background: '#16a085' }, fontSize: '12px' }}
                >
                  {loading ? 'Applying...' : 'Apply Transformation'}
                </Button>

                {wranglingResult && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    {wranglingResult.message}
                  </Alert>
                )}
              </>
            )}
          </div>
        </Collapse>
      </div>
    </Box>
  );
};

export default TableList;
